import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { MultiSelect } from "@/components/MultiSelect/MultiSelect";
import { Button } from "@/components/ui/button";
import { EntitySingleSelect } from "@/components/EntitySingleSelect/EntitySingleSelect";
import { EntityMultiSelect } from "@/components/EntityMultiSelect/EntityMultiSelect";
import { createEventType } from "@/modules/eventRegistration/features/EventTypeManagement/api/createEventType/createEventTypeApi";
import { fetchEventTypes } from "@/modules/eventRegistration/features/EventTypeManagement/api/fetchEventTypes/fetchEventTypesApi";
import { fetchEventCategories } from "@/api/fetchEventCategories/fetchEventCategoriesApi";
import { createEventTag } from "@/modules/eventRegistration/features/EventTagManagement/api/createEventTag/createEventTagApi";
import { fetchEventTags } from "@/modules/eventRegistration/features/EventTagManagement/api/fetchEventTags/fetchEventTagsApi";
import CreateUpdateEventLoadingSkeleton from "@/modules/eventRegistration/features/EventManagement/components/CreateUpdateEventLoadingSkeleton/CreateUpdateEventLoadingSkeleton";
import CustomCleaveInput from "@/components/CustomCleaveInput/CustomCleaveInput";
import type { CleaveOptions } from "cleave.js/options";
import { useEditEvent } from "@/modules/eventRegistration/features/EventManagement/hooks/useEditEvent";
import { validateEventCode } from "@/modules/eventRegistration/features/EventManagement/api/validateEventCode/validateEventCodeApi";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { Check, XCircle } from "lucide-react";
import { createEventCategory } from "@/api/createEventCategory/createEventCategoryApi";
import FilePickerDialog from "@/modules/hub/features/FileManagement/components/FilePickerDialog/FilePickerDialog";
import FileCollectionDisplay, {
  FileItem,
} from "@/modules/hub/features/FileManagement/components/FileCollectionDisplay/FileCollectionDisplay";
import ThumbnailDisplay from "@/modules/hub/features/FileManagement/components/ThumbnailDisplay/ThumbnailDisplay";
import { RichTextEditor } from "@/modules/hub/features/ResourceManagement/components/RichTextEditor/RichTextEditor";
import { ChevronLeftIcon } from "@heroicons/react/24/solid";

function EditEvent() {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();

  const {
    form,
    loadingMetadata,
    metadataError,
    loadingGet,
    getError,
    fetchedEvent,
    trades,
    businessRoles,
    loadingUpdate,
    submitForm,
    handleCancel,
  } = useEditEvent(uuid);

  const { control, handleSubmit, setValue, watch } = form;

  // Save button tracking
  const shouldRedirectRef = useRef(false);
  const [clickedButton, setClickedButton] = useState<
    "save" | "saveAndClose" | null
  >(null);

  // File picker dialog states
  const [isFilePickerOpen, setIsFilePickerOpen] = useState(false);
  const [isHeroImagePickerOpen, setIsHeroImagePickerOpen] = useState(false);
  const [isEventFilesPickerOpen, setIsEventFilesPickerOpen] = useState(false);

  // Track event files with full details including presigned URLs
  const [eventFiles, setEventFiles] = useState<FileItem[]>([]);

  // Track thumbnail display data separately from form data
  const [thumbnailDisplayUrl, setThumbnailDisplayUrl] = useState<string>("");
  const [thumbnailFileName, setThumbnailFileName] = useState<string>("");

  // Track hero image display data separately from form data
  const [heroImageDisplayUrl, setHeroImageDisplayUrl] = useState<string>("");
  const [heroImageFileName, setHeroImageFileName] = useState<string>("");

  const [codeCheckStatus, setCodeCheckStatus] = useState<
    "idle" | "loading" | "valid" | "invalid"
  >("idle");
  const [codeCheckMessage, setCodeCheckMessage] = useState("");

  // Reset clicked button state when loading completes
  useEffect(() => {
    if (!loadingUpdate) {
      setClickedButton(null);
    }
  }, [loadingUpdate]);

  // Initialize event files when fetched event data is loaded
  useEffect(() => {
    if (fetchedEvent && fetchedEvent.files && fetchedEvent.files.length > 0) {
      // Use the presigned URLs from the backend response
      const filesWithUrls: FileItem[] = fetchedEvent.files.map((file) => ({
        uuid: file.uuid,
        name: file.originalFileName || "Unknown file",
        url: file.fileUrl || undefined,
      }));

      setEventFiles(filesWithUrls);
    }
  }, [fetchedEvent]);

  // Set thumbnail and hero image display data when event is loaded
  useEffect(() => {
    if (fetchedEvent) {
      if (fetchedEvent.thumbnailUrl) {
        setThumbnailDisplayUrl(fetchedEvent.thumbnailUrl);
        setThumbnailFileName("Event thumbnail");
      } else {
        setThumbnailDisplayUrl("");
        setThumbnailFileName("");
      }

      if (fetchedEvent.heroImageUrl) {
        setHeroImageDisplayUrl(fetchedEvent.heroImageUrl);
        setHeroImageFileName("Hero image");
      } else {
        setHeroImageDisplayUrl("");
        setHeroImageFileName("");
      }
    }
  }, [fetchedEvent]);

  const cleavePriceOptions: CleaveOptions = useMemo(() => {
    return {
      numeral: true,
      numeralDecimalScale: 2,
      numeralThousandsGroupStyle: "thousand",
      prefix: "$",
      rawValueTrimPrefix: true,
      numeralPositiveOnly: true,
    };
  }, []);

  const eventCodeValue = watch("eventCode") || "";
  const debouncedEventCode = useDebouncedValue(eventCodeValue, 500);

  // Handle thumbnail file selection
  const handleThumbnailSelected = useCallback(
    (
      files: Array<{
        fileUuid: string;
        fileUrl: string;
        publicUrl: string;
        name: string;
      }>,
    ) => {
      if (files.length === 0) return;

      const file = files[0]; // Take only the first file for thumbnail

      // Set the thumbnail URL for display (using the public URL)
      setThumbnailDisplayUrl(file.publicUrl);
      setThumbnailFileName(file.name);

      // Set the thumbnailFileUuid for the backend
      setValue("thumbnailFileUuid", file.fileUuid, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      });

      // Close the file picker dialog
      setIsFilePickerOpen(false);
    },
    [setValue],
  );

  // Handle thumbnail removal
  const handleRemoveThumbnail = useCallback(() => {
    setThumbnailDisplayUrl("");
    setThumbnailFileName("");
    setValue("thumbnailFileUuid", null, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: false,
    });
  }, [setValue]);

  // Handle hero image selection
  const handleHeroImageSelected = useCallback(
    (
      files: Array<{
        fileUuid: string;
        fileUrl: string;
        publicUrl: string;
        name: string;
      }>,
    ) => {
      if (files.length === 0) return;
      const file = files[0];
      setHeroImageDisplayUrl(file.publicUrl);
      setHeroImageFileName(file.name);
      setValue("heroImageFileUuid", file.fileUuid, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      });
      setIsHeroImagePickerOpen(false);
    },
    [setValue],
  );

  // Handle hero image removal
  const handleRemoveHeroImage = useCallback(() => {
    setHeroImageDisplayUrl("");
    setHeroImageFileName("");
    setValue("heroImageFileUuid", null, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: false,
    });
  }, [setValue]);

  // Handle event files selection (multi-select)
  const handleEventFilesSelected = useCallback(
    (
      files: Array<{
        fileUuid: string;
        fileUrl: string;
        publicUrl: string;
        name: string;
      }>,
    ) => {
      if (files.length === 0) return;

      // Create file objects with public URLs
      const filesWithUrls: FileItem[] = files.map((file) => ({
        uuid: file.fileUuid,
        name: file.name,
        url: file.publicUrl, // Using the public URL
      }));

      // Get all the current fileUuids
      const currentFileUuids = form.getValues("fileUuids") || [];

      // Add the new file UUIDs
      const newFileUuids = [
        ...currentFileUuids,
        ...files.map((file) => file.fileUuid),
      ];

      // Update the form value
      setValue("fileUuids", newFileUuids, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      });

      // Store the file details for display
      setEventFiles((prev) => [...prev, ...filesWithUrls]);

      // Close the file picker dialog
      setIsEventFilesPickerOpen(false);
    },
    [form, setValue],
  );

  // Remove a single file from the selection
  const handleRemoveFile = useCallback(
    (fileUuid: string) => {
      // Update eventFiles state
      setEventFiles((prev) => prev.filter((file) => file.uuid !== fileUuid));

      // Update form fileUuids
      const currentFileUuids = form.getValues("fileUuids") || [];
      const updatedFileUuids = currentFileUuids.filter(
        (uuid) => uuid !== fileUuid,
      );
      setValue("fileUuids", updatedFileUuids, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      });
    },
    [form, setValue],
  );

  // Clear all files
  const handleClearAllFiles = useCallback(() => {
    setValue("fileUuids", [], {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: false,
    });
    setEventFiles([]);
  }, [setValue]);

  useEffect(() => {
    if (!debouncedEventCode) {
      setCodeCheckStatus("idle");
      setCodeCheckMessage("");
      return;
    }

    setCodeCheckStatus("loading");
    validateEventCode({
      eventCode: debouncedEventCode,
      eventUuid: uuid ?? null,
    })
      .then((res) => {
        if (res.data.codeExists) {
          setCodeCheckStatus("invalid");
          setCodeCheckMessage(
            res.data.message || "Event code is already in use.",
          );
        } else {
          setCodeCheckStatus("valid");
          setCodeCheckMessage("Event code is available!");
        }
      })
      .catch((err) => {
        console.error("Error validating event code:", err);
        setCodeCheckStatus("invalid");
        setCodeCheckMessage(
          "Error validating code. Please try again or choose another.",
        );
      });
  }, [debouncedEventCode, uuid, fetchedEvent]);

  if (loadingMetadata || loadingGet) {
    return <CreateUpdateEventLoadingSkeleton />;
  }

  if (metadataError) {
    return (
      <MainPageWrapper title="Edit Event">
        <p className="text-red-500">Error loading metadata: {metadataError}</p>
      </MainPageWrapper>
    );
  }
  if (getError) {
    return (
      <MainPageWrapper title="Edit Event">
        <p className="text-red-500">Error loading event: {getError}</p>
      </MainPageWrapper>
    );
  }
  if (!fetchedEvent) {
    return (
      <MainPageWrapper title="Edit Event">
        <p className="text-red-500">No event found.</p>
      </MainPageWrapper>
    );
  }

  return (
    <MainPageWrapper hideHeader>
      <div className="flex items-center gap-2 -mt-1 mb-3">
        <button
          className="p-1 -ml-1 rounded text-gray-600 hover:text-gray-900 dark:text-slate-300 dark:hover:text-slate-100 transition-colors"
          onClick={() => navigate("/admin/events")}
          type="button"
        >
          <ChevronLeftIcon className="h-5 w-5 stroke-current stroke-1" />
        </button>
        <h1 className="text-[15px] font-medium text-gray-900 dark:text-slate-100">
          Edit Event: {fetchedEvent.eventName}
        </h1>
      </div>
      <Form {...form}>
        <form
          className="space-y-8 pb-24"
          onSubmit={handleSubmit((values) =>
            submitForm(values, shouldRedirectRef.current),
          )}
        >
          <div className="text-sm text-muted-foreground mb-2">
            Fields marked with an asterisk (*) are required.
          </div>

          <FormField
            control={control}
            name="eventName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Event Name *</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="eventCode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Event Code *</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input {...field} />
                    {eventCodeValue && codeCheckStatus === "valid" && (
                      <Check className="absolute right-2 top-1/2 -translate-y-1/2 text-green-500" />
                    )}
                    {eventCodeValue && codeCheckStatus === "invalid" && (
                      <XCircle className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500" />
                    )}
                  </div>
                </FormControl>
                {codeCheckMessage && (
                  <p
                    className={`text-xs mt-1 ${
                      codeCheckStatus === "invalid"
                        ? "text-red-500"
                        : "text-green-500"
                    }`}
                  >
                    {codeCheckMessage}
                  </p>
                )}
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="eventDescription"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description *</FormLabel>
                <FormControl>
                  <RichTextEditor
                    id="eventDescription"
                    initialContent={fetchedEvent.eventDescription}
                    key={fetchedEvent.id}
                    onChange={(content) => {
                      field.onChange(content);
                    }}
                    onRemove={() => {}}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="eventPrice"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Price *</FormLabel>
                <FormControl>
                  <CustomCleaveInput
                    className="flex h-9 w-full rounded-md border border-input bg-white px-3 py-1 text-base shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100"
                    inputMode="decimal"
                    onChange={(rawValue) => {
                      field.onChange(rawValue);
                    }}
                    onPaste={(e) => e.preventDefault()}
                    options={cleavePriceOptions}
                    value={String(field.value ?? "")}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="isPublished"
            render={({ field }) => (
              <FormItem className="flex items-center justify-between rounded-lg border border-gray-200 dark:border-slate-700 p-4">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">Published</FormLabel>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="isVoucherEligible"
            render={({ field }) => (
              <FormItem className="flex items-center justify-between rounded-lg border border-gray-200 dark:border-slate-700 p-4">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">Voucher Eligible</FormLabel>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="isEligibleForLata"
            render={({ field }) => (
              <FormItem className="flex items-center justify-between rounded-lg border border-gray-200 dark:border-slate-700 p-4">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">Eligible for LATA</FormLabel>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="legacyCourseId"
            render={({ field }) => (
              <FormItem className="rounded-lg border border-gray-200 dark:border-slate-700 p-4">
                <FormLabel className="text-base">Legacy Course ID</FormLabel>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Member portal CourseId for deep-linking. Optional — leaves
                  user on the catalog/calendar landing page if blank.
                </p>
                <FormControl>
                  <Input
                    inputMode="numeric"
                    min={1}
                    onChange={(e) => {
                      const v = e.target.value;
                      field.onChange(v === "" ? null : Number(v));
                    }}
                    placeholder="e.g. 21"
                    type="number"
                    value={field.value ?? ""}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div>
            <FormLabel>Thumbnail</FormLabel>
            <div className="mt-2">
              <ThumbnailDisplay
                loading={false}
                onRemove={handleRemoveThumbnail}
                onSelect={() => setIsFilePickerOpen(true)}
                thumbnailFileName={thumbnailFileName}
                thumbnailUrl={thumbnailDisplayUrl}
              />
            </div>
          </div>

          <div>
            <FormLabel>Hero Image</FormLabel>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 mb-2">
              Displayed as a banner on the event landing page. Optimal size:
              1600×400px (4:1 ratio).
            </p>
            <ThumbnailDisplay
              loading={false}
              onRemove={handleRemoveHeroImage}
              onSelect={() => setIsHeroImagePickerOpen(true)}
              thumbnailFileName={heroImageFileName}
              thumbnailUrl={heroImageDisplayUrl}
            />
          </div>

          <FormField
            control={control}
            name="eventTypeId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Event Type</FormLabel>
                <FormControl>
                  <EntitySingleSelect
                    createEntity={async ({ name }) => {
                      const res = await createEventType({
                        name,
                        isActive: true,
                      });
                      return {
                        id: res.data.id ?? 0,
                        name: res.data.name ?? "",
                      };
                    }}
                    entityNamePlural="Event Types"
                    entityNameSingular="Event Type"
                    fetchEntities={async ({ searchTerm, page, pageSize }) => {
                      const sortBy = "name";
                      const sortOrder = "ASC";
                      const isActive = true;
                      const result = await fetchEventTypes({
                        name: searchTerm,
                        isActive,
                        page,
                        pageSize,
                        sortBy,
                        sortOrder,
                      });
                      const { eventTypes, totalCount } = result.data;
                      return {
                        data: eventTypes.map((et) => ({
                          id: et.id,
                          name: et.name,
                        })),
                        totalCount,
                      };
                    }}
                    onChange={field.onChange}
                    value={field.value || null}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="categories"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Event Categories</FormLabel>
                <FormControl>
                  <EntityMultiSelect
                    createEntity={async ({ name }) => {
                      const res = await createEventCategory({
                        name,
                        description: null,
                        isActive: true,
                      });
                      const { eventCategory } = res.data;
                      return {
                        id: eventCategory.id,
                        name: eventCategory.name,
                      };
                    }}
                    entityNamePlural="Event Categories"
                    entityNameSingular="Event Category"
                    fetchEntities={async ({ searchTerm, page, pageSize }) => {
                      const sortBy = "name";
                      const sortOrder = "ASC";
                      const isActive = true;
                      const result = await fetchEventCategories({
                        name: searchTerm,
                        isActive,
                        page,
                        pageSize,
                        sortBy,
                        sortOrder,
                      });
                      const { data, meta } = result;
                      return {
                        data: data.map((cat) => ({
                          id: cat.id,
                          name: cat.name,
                        })),
                        totalCount: meta.totalCount,
                      };
                    }}
                    onChange={field.onChange}
                    value={field.value || []}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="tradeIds"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Trades</FormLabel>
                <FormControl>
                  <MultiSelect
                    onChange={(vals) => field.onChange(vals.map(Number))}
                    options={trades.map((t) => ({
                      label: t.name,
                      value: String(t.id),
                    }))}
                    placeholder="Select trades..."
                    value={field.value?.map(String) ?? []}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="roles"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Job Titles</FormLabel>
                <FormControl>
                  <MultiSelect
                    onChange={(vals) =>
                      field.onChange(
                        vals
                          .map((val) => {
                            const role = businessRoles.find(
                              (r: { id: number; name: string }) =>
                                String(r.id) === val,
                            );
                            return role
                              ? { id: role.id, name: role.name }
                              : null;
                          })
                          .filter(Boolean),
                      )
                    }
                    options={businessRoles.map(
                      (r: { id: number; name: string }) => ({
                        label: r.name,
                        value: String(r.id),
                      }),
                    )}
                    placeholder="Select job titles..."
                    value={field.value?.map((r) => String(r.id)) ?? []}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="tags"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tags</FormLabel>
                <FormControl>
                  <EntityMultiSelect
                    createEntity={async ({ name }) => {
                      const res = await createEventTag({ name });
                      return {
                        id: res.data.id ?? 0,
                        name: res.data.name ?? "",
                      };
                    }}
                    entityNamePlural="Event Tags"
                    entityNameSingular="Event Tag"
                    fetchEntities={async ({ searchTerm, page, pageSize }) => {
                      const response = await fetchEventTags({
                        name: searchTerm,
                        page,
                        pageSize,
                        sortBy: "name",
                        sortOrder: "ASC",
                      });
                      const { tags, totalCount } = response.data;
                      return {
                        data: tags.map((t) => ({
                          id: t.id,
                          name: t.name,
                        })),
                        totalCount,
                      };
                    }}
                    onChange={field.onChange}
                    value={field.value || []}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="fileUuids"
            render={() => (
              <FormItem>
                <FormLabel>Event Files</FormLabel>
                <FormControl>
                  <FileCollectionDisplay
                    files={eventFiles}
                    loading={false}
                    onClearAll={handleClearAllFiles}
                    onRemoveFile={handleRemoveFile}
                    onSelectFiles={() => setIsEventFilesPickerOpen(true)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="sticky bottom-0 flex justify-end gap-4 px-4 py-4 border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-950 mt-8">
            <Button onClick={handleCancel} type="button" variant="outline">
              Cancel
            </Button>
            <Button
              disabled={loadingUpdate}
              onClick={() => {
                shouldRedirectRef.current = false;
                setClickedButton("save");
              }}
              type="submit"
            >
              {loadingUpdate && clickedButton === "save" ? "Saving..." : "Save"}
            </Button>
            <Button
              disabled={loadingUpdate}
              onClick={() => {
                shouldRedirectRef.current = true;
                setClickedButton("saveAndClose");
              }}
              type="submit"
            >
              {loadingUpdate && clickedButton === "saveAndClose"
                ? "Saving..."
                : "Save & Close"}
            </Button>
          </div>
        </form>
      </Form>

      {/* File Picker Dialog for Thumbnail (single select) */}
      <FilePickerDialog
        allowedFileTypes={["image/jpeg", "image/png", "image/gif"]}
        isOpen={isFilePickerOpen}
        multiSelect={false}
        onClose={() => setIsFilePickerOpen(false)}
        onSelect={handleThumbnailSelected}
        title="Select Thumbnail"
      />

      {/* File Picker Dialog for Hero Image (single select) */}
      <FilePickerDialog
        allowedFileTypes={["image/jpeg", "image/png", "image/gif"]}
        isOpen={isHeroImagePickerOpen}
        multiSelect={false}
        onClose={() => setIsHeroImagePickerOpen(false)}
        onSelect={handleHeroImageSelected}
        title="Select Hero Image"
      />

      {/* File Picker Dialog for Event Files (multi-select) */}
      <FilePickerDialog
        isOpen={isEventFilesPickerOpen}
        multiSelect={true}
        onClose={() => setIsEventFilesPickerOpen(false)}
        onSelect={handleEventFilesSelected}
        title="Select Event Files"
      />
    </MainPageWrapper>
  );
}

export default EditEvent;
